import type { ReactNode } from "react";
import { Link } from "react-router-dom";

/**
 * Campaign mentions in dashboard-chat messages.
 *
 * The composer lets the user tag a campaign with `@`; on send each visible
 * `@Name` is rewritten to the wire token `@[Name](campaign:<mongo id>)`, so
 * the name AND `_id` travel inside the message text itself — Amy reads both
 * on every later turn (no side channel to lose), and the persisted transcript
 * replays it. The user bubble renders the token back as a highlighted chip.
 */

export interface MentionableCampaign {
  _id: string;
  campaign_name: string;
  status?: string;
}

const TOKEN_RE = /@\[([^\]\n]+)\]\(campaign:([0-9a-fA-F]{24})\)/g;

const escapeRegex = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/** A completed `@Name` span inside the composer text. */
export interface ChipRange {
  start: number;
  /** Exclusive. */
  end: number;
  name: string;
}

/**
 * Completed `@Name` occurrences for the chosen names — the composer paints
 * these as chips and treats their `@` as sealed (not an open query). Longest
 * names first so a name that prefixes another can't split its chip; overlaps
 * are dropped.
 */
export function chipRangesIn(
  text: string,
  chosen: ReadonlyMap<string, string>
): ChipRange[] {
  const ranges: ChipRange[] = [];
  const names = [...chosen.keys()].sort((a, b) => b.length - a.length);
  for (const name of names) {
    const re = new RegExp(`@${escapeRegex(name)}(?!\\w)`, "g");
    for (const m of text.matchAll(re)) {
      const start = m.index;
      const end = start + m[0].length;
      if (!ranges.some((r) => start < r.end && end > r.start)) {
        ranges.push({ start, end, name });
      }
    }
  }
  return ranges.sort((a, b) => a.start - b.start);
}

/** The `@query` under the caret, or null when the caret isn't inside one. */
export function mentionQueryAt(
  text: string,
  caret: number,
  sealed: readonly ChipRange[] = []
): { start: number; query: string } | null {
  if (caret <= 0) return null;
  const start = text.lastIndexOf("@", caret - 1);
  if (start === -1 || start >= caret) return null;
  // Only a word-starting @ opens the dropdown — not emails like a@b.com.
  if (start > 0 && !/\s/.test(text[start - 1])) return null;
  // A completed chip's @ is sealed: the dropdown closes the moment a pick
  // completes the name (so the next Enter SENDS), and typing after a chip is
  // prose, not more query. Editing inside the chip un-seals it.
  if (sealed.some((r) => r.start === start)) return null;
  const query = text.slice(start + 1, caret);
  if (query.length > 40 || /[@\n]/.test(query)) return null;
  return { start, query };
}

/**
 * Rewrite each chosen `@Name` to its wire token. Longest names first so a
 * name that prefixes another can't clobber it; `(?!\w)` keeps `@NICA` out of
 * `@NICAx`. Square brackets are dropped from the label so the token stays
 * parseable — resolution rides the id, the label is display-only.
 */
export function withMentionTokens(
  text: string,
  chosen: ReadonlyMap<string, string>
): string {
  let out = text;
  const entries = [...chosen.entries()].sort((a, b) => b[0].length - a[0].length);
  for (const [name, id] of entries) {
    const label = name.replace(/[[\]]/g, "");
    out = out.replace(
      new RegExp(`@${escapeRegex(name)}(?!\\w)`, "g"),
      `@[${label}](campaign:${id})`
    );
  }
  return out;
}

/**
 * The composer's VISIBLE text layer: the textarea's own text is transparent
 * (caret kept), and this backdrop — identical metrics — renders it with each
 * completed mention as a primary chip. The chip's horizontal padding is
 * cancelled by an equal negative margin (and inline vertical padding never
 * moves line boxes), so glyph positions stay byte-identical to the textarea's
 * invisible text — otherwise the caret would drift after every chip.
 */
export function renderComposerHighlights(
  text: string,
  ranges: readonly ChipRange[]
): ReactNode {
  if (!ranges.length) return text;
  const nodes: ReactNode[] = [];
  let last = 0;
  ranges.forEach((r, i) => {
    if (r.start > last) nodes.push(text.slice(last, r.start));
    nodes.push(
      <span
        key={`chip-${i}`}
        className="-mx-1 rounded-xs bg-info/10 text-info px-1 py-0.5"
      >
        {text.slice(r.start, r.end)}
      </span>
    );
    last = r.end;
  });
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
}

/** Tokens → plain `@Name`, for conversation titles and text previews. */
export function stripMentionTokens(text: string): string {
  return text.replace(TOKEN_RE, "@$1");
}

/** Message text with each mention token rendered as a campaign-page chip. */
export function renderMentions(text: string): ReactNode {
  if (!text.includes("](campaign:")) return text;
  const nodes: ReactNode[] = [];
  let last = 0;
  let key = 0;
  for (const m of text.matchAll(TOKEN_RE)) {
    if (m.index > last) nodes.push(text.slice(last, m.index));
    nodes.push(
      <Link
        key={`mention-${key++}`}
        to={`/dashboard/campaigns/${m[2]}`}
        className="rounded-xs bg-info/10 text-info px-1 font-medium underline-offset-2 hover:underline"
      >
        @{m[1]}
      </Link>
    );
    last = m.index + m[0].length;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
}
